<template>
  <div class="wrapper w-100">
    <survey-question-checklist
      v-if="preview && !isNew"
      :question="value"
    ></survey-question-checklist>
    <div
      v-if="preview && !isNew"
      class="clickLayer text-right rounded"
      @click.stop="edit"
    >
      <div
        class="mt-3 mr-2 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="edit"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Question</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('delete')"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete Question</span>
        </v-tooltip>
      </div>
      <div
        class="mt-3 mr-3 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top v-if="questionIndex !== 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', -1)"
            >
              <v-icon small>$sortUp</v-icon>
            </v-btn>
          </template>
          <span>Move Up</span>
        </v-tooltip>
        <v-tooltip top v-if="questionIndex !== page.questions.length - 1">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', 1)"
            >
              <v-icon small>$sortDown</v-icon>
            </v-btn>
          </template>
          <span>Move Down</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog
      v-model="openEditor"
      scrollable
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      transition="dialog-bottom-transition"
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card rounded="0">
          <div class="elevation-3 primary">
            <v-container class="pa-0">
              <dialog-title
                :title="`${value.key === '' ? 'Add' : 'Edit'} Question`"
                class="white--text pl-md-0"
                @close="onCancel"
              >
                <template v-slot:actions="{ close }">
                  <v-btn dark icon @click="close">
                    <v-icon>$close</v-icon></v-btn
                  >
                </template>
              </dialog-title>
            </v-container>
          </div>
          <v-card-text ref="body">
            <v-container>
              <v-row>
                <v-col cols="12">
                  Fill out the form below to change the question.
                </v-col>
                <v-col cols="12">
                  <survey-editor-page-select
                    v-model="innerPageKey"
                    :pages="pages"
                    :locked="locked"
                  ></survey-editor-page-select>
                </v-col>
                <v-col cols="4">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: allKeys.filter((x) => x !== value.key),
                    }"
                    v-slot="{ errors }"
                    vid="key"
                  >
                    <v-text-field
                      label="Key*"
                      v-model="innerValue.key"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint="Cannot be changed once added."
                      :autofocus="isNew"
                      :disabled="!isNew || locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="4">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: allMappingKeys.filter(
                        (x) => x !== value.mappingKey
                      ),
                    }"
                    v-slot="{ errors }"
                    vid="key"
                  >
                    <v-text-field
                      label="Mapping Key*"
                      v-model="innerValue.mappingKey"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint="Used to map data across multiple surveys"
                      :autofocus="isNew"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="4">
                  <ValidationProvider
                    rules="required|min:2|max:500"
                    v-slot="{ errors }"
                    vid="Type"
                  >
                    <v-select
                      label="Type*"
                      v-model="innerValue.type"
                      :items="types"
                      outlined
                      :error-messages="errors"
                      disabled
                      rows="3"
                    ></v-select>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="1" lg="2" md="3">
                  <ValidationProvider
                    rules="max:200"
                    v-slot="{ errors }"
                    vid="QuestionNumber"
                  >
                    <v-text-field
                      label="Number"
                      v-model="innerValue.questionNumber"
                      outlined
                      :error-messages="errors"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="11" lg="10" md="9">
                  <ValidationProvider
                    rules="required|min:2|max:2000"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-textarea
                      label="Question*"
                      v-model="innerValue.text"
                      outlined
                      :error-messages="errors"
                      rows="3"
                      :disabled="locked"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="ResponseCountTarget"
                  >
                    <v-text-field
                      v-model="innerValue.responseCountTarget"
                      type="number"
                      label="Response Goal"
                      hint="Used to calculate a question's response rate goals. Only needed if the goal is different than the base survey response goal."
                      persistent-hint
                      outlined
                      :error-messages="errors"
                      :disabled="locked || optionGoals"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="required = !required"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ required ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div @click="required = !required" class="pointer">
                      Required
                    </div>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="isPrivate = !isPrivate"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ isPrivate ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div @click="isPrivate = !isPrivate" class="pointer">
                      Hide on Public Reporting
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="d-flex align-center">
                    <div class="flex-fill">
                      <div class="text-h6">Checklist Options</div>
                      <p class="text-body-2">
                        Use the grid below to add, edit, or remove checklist
                        options in the question.
                      </p>
                    </div>
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          fab
                          color="primary"
                          small
                          elevation="0"
                          @click="
                            () => {
                              cancelAddQuestionRow();
                              addQuestionRow();
                            }
                          "
                          :disabled="
                            locked ||
                              addingRow ||
                              editingRow > -1 ||
                              innerValue.children.length === 0
                          "
                          ><font-awesome-icon icon="plus"></font-awesome-icon
                        ></v-btn>
                      </template>
                      <span>Add Option</span>
                    </v-tooltip>
                  </div>
                  <table class="stripped">
                    <thead>
                      <td
                        class="py-2 w-100 text-left grey darken-3 white--text"
                      >
                        Text
                      </td>
                      <td
                        class="py-2 text-left text-left grey darken-3 white--text"
                      >
                        Key
                      </td>
                      <td
                        class="py-2 text-left text-left grey darken-3 white--text"
                      >
                        Mapping Key
                      </td>
                      <td
                        class="py-2 text-center text-left grey darken-3 white--text"
                      >
                        Actions
                      </td>
                    </thead>
                    <template v-for="(child, index) in innerValue.children">
                      <tr
                        :key="`read_${index}`"
                        v-if="editingRow !== index && deletingRow !== index"
                        @click="
                          () => {
                            if (editingRow == -1 && !addingRow && !locked) {
                              editQuestionRow(child, index);
                            }
                          }
                        "
                        :class="{
                          'hover pointer':
                            editingRow == -1 && !addingRow && !locked,
                          'grey--text': editingRow > -1 || addingRow,
                        }"
                      >
                        <td class="py-3 px-4">
                          <survey-question-text
                            class="d-inline"
                            :text="child.text"
                          ></survey-question-text>
                        </td>
                        <td class="py-3 px-4">
                          {{ child.key }}
                        </td>
                        <td class="py-3 px-4">
                          {{ child.mappingKey }}
                        </td>
                        <td class="text-no-wrap py-3 px-4">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  addingRow || editingRow > -1 || locked
                                "
                                @click.stop="editQuestionRow(child, index)"
                              >
                                <v-icon small>$edit</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  addingRow || editingRow > -1 || locked
                                "
                                @click.stop="deleteQuestionRow(child, index)"
                              >
                                <v-icon small>$delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  index === 0 ||
                                    addingRow ||
                                    editingRow > -1 ||
                                    locked
                                "
                                @click.stop="moveRow(index, -1)"
                              >
                                <v-icon small>$sortUp</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Up</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                :disabled="
                                  index + 1 == innerValue.children.length ||
                                    addingRow ||
                                    editingRow > -1 ||
                                    locked
                                "
                                @click.stop="moveRow(index, 1)"
                              >
                                <v-icon small>$sortDown</v-icon>
                              </v-btn>
                            </template>
                            <span>Move Down</span>
                          </v-tooltip>
                        </td>
                      </tr>
                      <ValidationObserver
                        v-slot="{ invalid }"
                        :key="`edit_${index}`"
                        v-if="editingRow === index"
                        tag="tr"
                        ref="editRowObserver"
                      >
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            rules="required|min:2|max:2000"
                            v-slot="{ errors }"
                            vid="questionRowText"
                          >
                            <v-textarea
                              label="Text*"
                              v-model="editRow.text"
                              outlined
                              rows="2"
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionRow"
                              @keydown.esc="cancelQuestionRow"
                              :disabled="locked"
                            ></v-textarea>
                          </ValidationProvider>
                        </td>
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            :rules="{
                              required: true,
                              min: 2,
                              max: 100,
                              notTaken: [innerValue.key].concat(
                                allKeys.filter(
                                  (x) => x !== child.key && x !== value.key
                                )
                              ),
                            }"
                            v-slot="{ errors }"
                            vid="questionRowKey"
                          >
                            <v-text-field
                              label="Key*"
                              v-model="editRow.key"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionRow"
                              @keydown.esc="cancelQuestionRow"
                              style="width: 150px"
                              disabled
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="px-4 py-4" style="vertical-align: top">
                          <ValidationProvider
                            :rules="{
                              required: true,
                              min: 2,
                              max: 100,
                              notTaken: [innerValue.mappingKey].concat(
                                allMappingKeys.filter(
                                  (x) =>
                                    x !== child.mappingKey &&
                                    x !== value.mappingKey
                                )
                              ),
                            }"
                            v-slot="{ errors }"
                            vid="questionRowMappingKey"
                          >
                            <v-text-field
                              label="Mapping Key*"
                              v-model="editRow.mappingKey"
                              outlined
                              :error-messages="errors"
                              :hide-details="errors.length == 0"
                              @keydown.enter.prevent="confirmQuestionRow"
                              @keydown.esc="cancelQuestionRow"
                              style="width: 150px"
                              :disabled="locked"
                            ></v-text-field>
                          </ValidationProvider>
                        </td>
                        <td class="text-no-wrap px-4 py-4 text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                @click="confirmQuestionRow"
                                color="green darken-2"
                                :disabled="invalid || locked"
                                class="mr-2"
                                large
                              >
                                <v-icon small>$check</v-icon>
                              </v-btn>
                            </template>
                            <span>Confirm</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="red darken-2"
                                v-bind="attrs"
                                v-on="on"
                                icon
                                outlined
                                @click="cancelQuestionRow"
                              >
                                <v-icon small>$close</v-icon>
                              </v-btn>
                            </template>
                            <span>Cancel</span>
                          </v-tooltip>
                        </td>
                      </ValidationObserver>
                      <tr :key="`delete_${index}`" v-if="deletingRow === index">
                        <td colspan="4" class="red lighten-4 red--text py-3">
                          <div class="d-flex align-center">
                            <div class="flex-fill">
                              You are about to delete
                              <strong>{{ child.key }}</strong
                              >. Are you sure?
                            </div>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="confirmDeleteQuestionRow"
                                  color="red darken-2 white--text"
                                  class="mr-2"
                                >
                                  Yes, Delete
                                </v-btn>
                              </template>
                              <span>Confirm</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="cancelDeleteQuestionRow"
                                >
                                  Cancel
                                </v-btn>
                              </template>
                              <span>Cancel</span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <ValidationObserver
                      v-if="
                        addingRow ||
                          (innerValue.children &&
                            innerValue.children.length === 0)
                      "
                      v-slot="{ invalid }"
                      tag="tr"
                      ref="addRowObserver"
                    >
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          rules="required|min:2|max:2000"
                          v-slot="{ errors }"
                          vid="addQuestionRowText"
                        >
                          <v-textarea
                            label="Text*"
                            v-model="addRow.text"
                            outlined
                            rows="2"
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            :autofocus="!isNew"
                            @keydown.enter.prevent="confirmAddQuestionRow"
                            @keydown.esc="cancelAddQuestionRow"
                            :disabled="locked"
                          ></v-textarea>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          :rules="{
                            required: true,
                            min: 2,
                            max: 100,
                            notTaken: [innerValue.key].concat(
                              allKeys.filter((x) => x !== value.key)
                            ),
                          }"
                          v-slot="{ errors }"
                          vid="addQuestionRowKey"
                        >
                          <v-text-field
                            label="Key*"
                            v-model="addRow.key"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="confirmAddQuestionRow"
                            @keydown.esc="cancelAddQuestionRow"
                            :disabled="locked"
                            style="width: 150px"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="px-4 py-4" style="vertical-align: top">
                        <ValidationProvider
                          :rules="{
                            required: true,
                            min: 2,
                            max: 100,
                            notTaken: [innerValue.mappingKey].concat(
                              allMappingKeys.filter(
                                (x) => x !== value.mappingKey
                              )
                            ),
                          }"
                          v-slot="{ errors }"
                          vid="addQuestionRowMappingKey"
                        >
                          <v-text-field
                            label="Mapping Key*"
                            v-model="addRow.mappingKey"
                            outlined
                            :error-messages="errors"
                            :hide-details="errors.length == 0"
                            @keydown.enter.prevent="confirmAddQuestionRow"
                            @keydown.esc="cancelAddQuestionRow"
                            :disabled="locked"
                            style="width: 150px"
                          ></v-text-field>
                        </ValidationProvider>
                      </td>
                      <td class="text-no-wrap px-4 py-4 text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              @click="confirmAddQuestionRow"
                              color="green darken-2"
                              :disabled="invalid || locked"
                              class="mr-2"
                              large
                            >
                              <v-icon small>$check</v-icon>
                            </v-btn>
                          </template>
                          <span>Confirm</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              color="red darken-2"
                              v-bind="attrs"
                              v-on="on"
                              icon
                              outlined
                              large
                              v-if="
                                innerValue.children &&
                                  innerValue.children.length > 0
                              "
                              @click="cancelAddQuestionRow"
                              :disabled="locked"
                            >
                              <v-icon small>$close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancel</span>
                        </v-tooltip>
                      </td>
                    </ValidationObserver>
                  </table>
                  <v-divider></v-divider>
                </v-col>
                <v-col xl="2" lg="3" md="4" sm="6" class="pt-6">
                  <ValidationProvider
                    :rules="
                      'required|numeric' + (required ? '|min_value:1' : '')
                    "
                    v-slot="{ errors }"
                    vid="settingMin"
                  >
                    <v-text-field
                      label="Minimum Selection*"
                      v-model="
                        innerValue.settings.find(
                          (x) => x.key == questionSettings.min.key
                        ).value
                      "
                      outlined
                      class="align-input-right"
                      :error-messages="errors"
                      persistent-hint
                      hint="Use this value to set the minimum number of selections"
                      :disabled="locked"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col xl="2" lg="3" md="4" sm="6" class="pt-6">
                  <ValidationProvider
                    :rules="
                      `required|numeric|min_value:${min}|max_value:${optionCount}`
                    "
                    v-slot="{ errors }"
                    vid="settingMax"
                  >
                    <v-text-field
                      label="Maximum Selection*"
                      v-model="
                        innerValue.settings.find(
                          (x) => x.key == questionSettings.max.key
                        ).value
                      "
                      outlined
                      class="align-input-right"
                      :error-messages="errors"
                      persistent-hint
                      hint="Use this value to set the maximum number of selections"
                      :disabled="locked"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-h6">Conditional Rules</div>
                  <p class="text-body-2">
                    Use the section below to create conditional rules for your
                    question.
                  </p>

                  <question-rules
                    v-for="rule in rules"
                    :key="rule.key"
                    :label="rule.label"
                    :ruleKey="rule.key"
                    :queryRules="queryRules"
                    v-model="innerValue.rules"
                    :locked="locked"
                    class="mb-4"
                  ></question-rules>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 py-0 elevation-3">
            <v-container class="">
              <div class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn text color="primary" @click="onCancel"> Cancel </v-btn>
                <v-btn
                  type="submit"
                  color="primary"
                  v-if="!locked"
                  :disabled="addingRow || editingRow > -1"
                >
                  Ok
                </v-btn>
              </div>
              <div
                v-if="addingRow"
                class="text-caption red--text darken-2 d-flex align-center justify-end"
              >
                <v-icon x-small class="red--text darken-2 mr-1">$error</v-icon>
                You must finish adding your
                <strong class="mr-1">Checklist Option</strong>
                before saving.
              </div>
              <div
                v-if="editingRow > -1"
                class="text-caption red--text darken-2 d-flex align-center justify-end"
              >
                <v-icon x-small class="red--text darken-2 mr-1">$error</v-icon>
                You must finish editing your
                <strong class="mr-1">Checklist Option</strong>
                before saving.
              </div>
            </v-container>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  .clickLayer {
    opacity: 0;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    transition: opacity 300ms ease-in-out;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }
}
</style>

<script>
import Vue from "vue";
import DialogTitle from "./DialogTitle.vue";
import SurveyQuestionChecklist from "./Survey.Question.Checklist.vue";
import {
  questionTypeOptions,
  questionSettings,
  getQuestionSetting,
  ruleTypes,
} from "../constants/Survey";
import equal from "deep-is";
import SurveyEditorPageSelect from "./Survey.Editor.PageSelect.vue";
import QuestionRules from "./QuestionRules.vue";
import SurveyQuestionText from "./Survey.Question.Text.vue";

const newRow = () => ({
  questionNumber: "",
  text: "",
  key: "",
  mappingKey: "",
});

export default {
  name: "ChecklistEditor",
  components: {
    DialogTitle,
    SurveyQuestionChecklist,
    SurveyEditorPageSelect,
    QuestionRules,
    SurveyQuestionText,
  },
  data: () => ({
    innerValue: {
      settings: [],
    },
    innerPageKey: "",
    openEditor: false,
    addingRow: false,
    addRow: newRow(),
    editingRow: -1,
    editRow: newRow(),
    deletingRow: -1,
    questionSettings,
  }),
  props: {
    preview: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
    questionIndex: {
      type: Number,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    pageKey: {
      type: String,
      required: true,
    },
    questionKeys: {
      type: Array,
      required: true,
    },
    questionMappingKeys: {
      type: Array,
      required: true,
    },
    queryRules: {
      type: Array,
      required: true,
    },
    locked: {
      type: Boolean,
    },
  },
  computed: {
    types() {
      return questionTypeOptions;
    },
    pageOptions() {
      return this.pages.map((x) => {
        return {
          text: x.displayName,
          value: x.key,
        };
      });
    },
    canMoveUp() {
      return this.questionIndex != 0;
    },
    page() {
      return this.pages.find((x) => x.key === this.pageKey);
    },
    allMappingKeys() {
      let results = this.questionMappingKeys.slice();
      let rowKeys = this.innerValue.children
        ? this.innerValue.children.map((x) => x.mappingKey)
        : [];

      for (let i = 0; i < rowKeys.length; i++) {
        if (!results.some((x) => x === rowKeys[i])) results.push(rowKeys[i]);
      }

      return results;
    },
    allKeys() {
      let results = this.questionKeys.slice();
      let rowKeys = this.innerValue.children
        ? this.innerValue.children.map((x) => x.key)
        : [];

      for (let i = 0; i < rowKeys.length; i++) {
        if (!results.some((x) => x === rowKeys[i])) results.push(rowKeys[i]);
      }

      return results;
    },
    isNew() {
      return this.value.key === "";
    },
    columnWidth() {
      return getQuestionSetting(
        this.innerValue.settings,
        questionSettings.columnWidth.key
      );
    },
    rules() {
      let rules = [];

      if (!this.required)
        rules.push({
          label: "Required If",
          key: ruleTypes.requiredIf,
        });

      rules.push({
        label: "Optional If",
        key: ruleTypes.optionalIf,
      });

      rules.push({
        label: "Show If",
        key: ruleTypes.showIf,
      });

      rules.push({
        label: "Hide If",
        key: ruleTypes.hideIf,
      });

      return rules;
    },
    required: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.required.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.required.key
        ).value = val + "";
      },
    },
    isPrivate: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.private.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.private.key
        ).value = val + "";
      },
    },
    optionCount() {
      return this.innerValue?.children?.length ?? 0;
    },
    min() {
      return (
        this.innerValue.settings.find((x) => x.key == questionSettings.min.key)
          ?.value ?? 0
      );
    },
    max() {
      return (
        this.innerValue.settings.find((x) => x.key == questionSettings.max.key)
          ?.value ?? 0
      );
    },
  },
  watch: {
    value(val) {
      if (!equal(val, this.innerValue)) {
        this.applyDefaults();
      }
    },
    pageKey(value) {
      if (value != this.innerPageKey)
        this.innerPageKey = JSON.parse(JSON.stringify(value));
    },
    openEditor(val) {
      if (val) {
        this.innerValue = JSON.parse(JSON.stringify(this.value));

        this.addingRow = false;
        this.addRow = newRow();
        this.editingRow = -1;
        this.editRow = newRow();
        this.deletingRow = -1;

        if (this.pageKey != this.innerPageKey)
          this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));

        this.optionGoals = this.hasOptionGoals ? true : false;

        if (this.$refs.body) {
          setTimeout(() => (this.$refs.body.scrollTop = 0), 1);
        }
      }
    },
  },
  methods: {
    edit() {
      if (this.$refs.observer) this.$refs.observer.reset();
      this.openEditor = true;
    },
    onSubmit() {
      this.cancelAddQuestionRow();
      this.$nextTick(() => {
        this.$refs.observer.validate().then((isValid) => {
          if (isValid) {
            if (this.innerValue.responseCountTarget === "") {
              this.innerValue.responseCountTarget = null;
            }

            this.$emit("input", this.innerValue);
            if (this.pageKey != this.innerPageKey) {
              this.$emit("set-page", this.innerPageKey);
            }
            this.openEditor = false;
          }
        });
      });
    },
    editQuestionRow(row, index) {
      this.editRow = JSON.parse(JSON.stringify(row));
      this.editingRow = index;
    },
    cancelQuestionRow() {
      this.editRow = newRow();
      this.editingRow = -1;
    },
    confirmQuestionRow() {
      this.$refs.editRowObserver[0].validate().then((isValid) => {
        if (isValid) {
          Vue.set(this.innerValue.children, this.editingRow, this.editRow);
          this.cancelQuestionRow();
        }
      });
    },
    addQuestionRow() {
      this.addRow = newRow();
      this.addingRow = true;
    },
    cancelAddQuestionRow() {
      this.addRow = newRow();
      this.addingRow = false;
    },
    confirmAddQuestionRow() {
      this.$refs.addRowObserver.validate().then((isValid) => {
        if (isValid) {
          this.innerValue.children.push(this.addRow);
          this.cancelAddQuestionRow();
        }
      });
    },
    deleteQuestionRow(row, index) {
      this.deletingRow = index;
    },
    cancelDeleteQuestionRow() {
      this.deletingRow = -1;
    },
    confirmDeleteQuestionRow() {
      this.innerValue.children.splice(this.deletingRow, 1);
      this.cancelDeleteQuestionRow();
    },
    moveRow(index, increment) {
      let mirror = JSON.parse(JSON.stringify(this.innerValue));
      let old = mirror.children[index + increment];

      mirror.children[index + increment] = mirror.children[index];
      mirror.children[index] = old;

      this.innerValue = mirror;
    },
    applyDefaults() {
      if (this.value) {
        let mirror = JSON.parse(JSON.stringify(this.value));
        let changed = false;

        let defaultSettings = [
          {
            key: questionSettings.required.key,
            value: questionSettings.required.default,
          },
          {
            key: questionSettings.min.key,
            value: questionSettings.min.default,
          },
          {
            key: questionSettings.max.key,
            value: questionSettings.max.default,
          },
          {
            key: questionSettings.private.key,
            value: questionSettings.private.default,
          },
        ];

        if (!mirror.settings) {
          mirror.settings = [];
          changed = true;
        }

        for (let i = 0; i < defaultSettings.length; i++) {
          if (!mirror.settings.some((x) => x.key === defaultSettings[i].key)) {
            mirror.settings.push(defaultSettings[i]);
            changed = true;
          }
        }

        if (changed) this.$emit("input", mirror);
        this.innerValue = JSON.parse(JSON.stringify(mirror));
      }
    },
    onCancel() {
      if (this.isNew) this.$emit("force-delete");
      this.openEditor = false;
    },
  },
  mounted() {
    if (this.pageKey)
      this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));
    this.applyDefaults();
  },
};
</script>
